import { useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toolbar } from 'primereact/toolbar';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { Menu } from 'primereact/menu';
import * as generic from '../../../components/Generico';
import { useLazyParams } from '../../../hooks/useLazyParams';
import { TablaDinamica } from '../../../components/TablaDinamica';
import { AnexosComponent } from '../../../components/AnexosComponent';
import { useToken } from '../../../components/useToken';
import { FiltroMultiempresa } from '../../../components/FiltroMultiempresa';
import { DialogInstrucciones } from '../../../components/ui/DialogInstrucciones';
import { AccionistaPn as AccionistaPnModel } from '../../../models/AccionistaPn';
import { AccionistaService } from '../../../service/AccionistaService';
import { DatosGenerales } from './DatosGenerales';
import { InformacionEconomica } from './InformacionEconomica';
import { Pep } from './Pep';
import { Declaracion } from './Declaracion';

// Exportación limpia sin el React.memo antiguo
export const Accionista = () => {
    const [catalogos, setCatalogos] = useState(null);
    const omodel = new AccionistaPnModel({ formularioMatriz: catalogos?.formularioMatriz });
    const toast = useRef(null);
    const accionistaSrv = new AccionistaService();
    const { getPerss, getRol } = useToken();
    // Estados globales del formulario
    const [accionista, setAccionista] = useState(null);
    const [permss, setPermss] = useState(null);
    // Estados de navegación y UI
    const [dialogAccionista, setDialogAccionista] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [siCliente, setSiCliente] = useState(false);
    const [urls, setUrls] = useState(null);
    const [dialogAnexos, setDialogAnexos] = useState(false);
    const [dialogInstruccion, setDialogInstruccion] = useState(false);
    const [soloLectura, setSoloLectura] = useState(false);
    const [empresa, setEmpresa] = useState(null);
    const tablaEstado = useLazyParams({ modelo: omodel.model });
    const menuRef = useRef(null);
    const topRef = useRef(null);
    const [mostrarRegresarFlotante, setMostrarRegresarFlotante] = useState(false);

    useEffect(() => {
        setUrls({
            urlListado: 'ficha/accionista',
            urlDownload: 'ficha/accionista/descarga',
            urlUpload: 'ficha/accionista/store',
            udelete: 'ficha/accionista/eliminar-anexo',
            uactual: 'ficha/accionista/editar',
            uenviar: 'ficha/accionista/enviar',
            uaprobar: 'ficha/accionista/aprobar',
        });

        const permisosToken = getPerss();
        const esClienteNotificado = permisosToken !== undefined;
        setSiCliente(esClienteNotificado);

        if (esClienteNotificado) { // FLUJO CLIENTE NOTIFICADO
            setPermss(permisosToken);
            editFicha(true);
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (getPerss() === undefined) {
            loadLazyData();
        }
        // eslint-disable-next-line
    }, [tablaEstado.lazyParams]);

    // Efecto para observar el inicio del formulario
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setMostrarRegresarFlotante(!entry.isIntersecting);
            },
            { threshold: 0 }
        );
        const currentRef = topRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }
        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [dialogAccionista]);

    const loadLazyData = () => {
        tablaEstado.setLoading(true);
        const _lazyParams = { ...tablaEstado.lazyParams };
        delete _lazyParams.rtipo;
        delete _lazyParams.empresa;
        if (permss == null) {
            _lazyParams.rtipo = true;
        }
        if (empresa) {
            _lazyParams.empresa = empresa;
        }
        const rol = getRol();
        if (!rol) {
            toast.current.show({ severity: 'warn', summary: 'Rol no seleccionado', detail: 'Por favor selecciona un rol antes de continuar.', life: 3000 });
            tablaEstado.setLoading(false);
            return;
        } else {
            _lazyParams.rol = rol;
        }
        accionistaSrv.getAccionista({ lazyEvent: JSON.stringify(_lazyParams) }).then(res => {
            tablaEstado.setTotalRecords(res.data.total);
            tablaEstado.setData(res.data.registros);
            if (res.data.perss) {
                setPermss(res.data.perss);
            }
            if (res.data.catalogos) {
                setCatalogos(res.data.catalogos);
            }
        }).finally(() => {
            tablaEstado.setLoading(false);
        });
    }

    const iniciarNuevo = () => {
        setSoloLectura(false);
        setAccionista(null);
        setActiveIndex(0);
        setDialogAccionista(true);
        setDialogInstruccion(true);
    }

    const editFicha = (esNotificado = false, isReadOnly = false) => {
        setSoloLectura(isReadOnly);
        const idConsulta = esNotificado ? 0 : accionista?.id;
        const necesitaCatalogos = catalogos === null;

        // Firma cambiada para adaptarse al nuevo estándar del Service
        accionistaSrv.consultarAccionista(idConsulta, null, { necesitaCatalogos: necesitaCatalogos }).then(res => {
            if (necesitaCatalogos && res.data.catalogos) {
                setCatalogos(res.data.catalogos);
            }
            if (!res.data.accionista) {
                iniciarNuevo();
                return;
            }

            setDialogAccionista(true);
            setActiveIndex(0);
            setDialogInstruccion(true);

            nuevoAccionista({ id: res.data.accionista.id, nemonico: res.data.accionista.nemonico });
        });
    }

    const confirmDelete = () => {
        confirmDialog({
            message: '¿Está seguro de eliminar el formulario?',
            header: 'Confirmación',
            icon: 'pi pi-exclamation-triangle',
            accept: () => deleteAccionista()
        });
    }

    const deleteAccionista = () => {
        accionistaSrv.eliminarAccionista(accionista?.id).then(res => {
            loadLazyData();
            generic.funcSuccess(res, toast);
        }).catch(error => {
            generic.funcDeleteError(error, toast);
        });
    }

    // FUNCIÓN VITAL PARA MANTENER SINCRONIZADAS LAS SECCIONES
    const nuevoAccionista = (nuevo) => {
        if (!nuevo) {
            setAccionista(null);
            return;
        }
        setAccionista(prev => ({ ...prev, ...nuevo }));
    }

    // CONTROLES DE NAVEGACIÓN (Siguiente / Anterior)
    const handleNext = () => {
        if (activeIndex < 3) setActiveIndex(activeIndex + 1);
    };

    const handlePrev = () => {
        if (activeIndex > 0) setActiveIndex(activeIndex - 1);
    };

    const regresar = () => {
        setActiveIndex(0);
        setDialogAccionista(false);
        setSoloLectura(false);
        if (!siCliente) loadLazyData();
    }

    const reiniciar = () => {
        if (!siCliente) loadLazyData();
        setDialogAccionista(false);
        setDialogAnexos(false);
    }

    const itemsMenu = () => {
        const items = [];
        if (permss?.consultar) {
            items.push({ label: 'Ver', icon: 'pi pi-eye', command: () => { editFicha(false, true); } });
        }
        if (permss?.actualizar && (!accionista?.nemonico || accionista?.nemonico !== 'FINALIZADO')) {
            items.push({ label: 'Editar', icon: 'pi pi-pencil', command: () => { editFicha(); } });
        }
        if (permss?.consultar) {
            items.push({ label: 'Anexos formulario', icon: 'pi pi-folder-open', command: () => { setDialogAnexos(true); } });
        }
        if (permss?.eliminar && (!accionista?.nemonico || accionista?.nemonico !== 'FINALIZADO')) {
            items.push({ label: 'Eliminar', icon: 'pi pi-trash', command: () => { confirmDelete(); } });
        }
        return [{ label: 'Opciones', items: items }];
    }

    const accionesTemplate = (rowData) => {
        return (
            <Button icon="pi pi-caret-down" type="button" className="p-button-rounded p-button-success mr-2" title="Opciones/Acciones" aria-controls="popup_menu" aria-haspopup
                onClick={(event) => { setAccionista(rowData); menuRef.current.toggle(event); }} />
        );
    };

    const ColumnaAcciones = (
        <Column header="Acciones" body={accionesTemplate} style={{ width: '6rem', textAlign: 'center' }} />
    );

    const panelHeaderTemplate = () => (
        <div className="p-panel-header flex justify-content-between align-items-center flex-wrap gap-2 w-full">
            <div>
                <h3 className="m-0 text-xl font-bold p-text-secondary flex align-items-center">
                    <i className="pi pi-user mr-2"></i>
                    <span className="mr-2">{soloLectura ? 'VER' : (accionista?.id ? 'EDITAR' : 'NUEVO')} FORMULARIO DE ACCIONISTA PERSONA NATURAL</span>
                    <Button type="button" icon="pi pi-question-circle" className="p-button-rounded p-button-text p-button-sm p-0 m-0 text-white hover:text-blue-200 transition-colors transition-duration-200"
                        style={{ width: '2rem', height: '2rem' }} onClick={() => setDialogInstruccion(true)} tooltip="Ver instrucciones" tooltipOptions={{ position: 'right' }} />
                </h3>
            </div>
            {!siCliente && (
                <Button tooltip="Regresar al listado" tooltipOptions={{ position: 'left' }} icon="pi pi-arrow-left" onClick={regresar} />
            )}
        </div>
    );

    return (
        <>
            <ConfirmDialog />
            <Toast ref={toast} />
            <Menu model={itemsMenu()} popup ref={menuRef} id="popup_menu" />
            {!dialogAccionista && !siCliente && (
                <div className="fadein animation-duration-300">
                    {getRol() && getRol()?.ideg && <FiltroMultiempresa loadLazyData={loadLazyData} setEmpresa={setEmpresa} />}
                    <Toolbar className="mb-4 shadow-1 border-round" start={(getRol() && getRol()?.ideg) ? null : generic.leftToolbarTemplate(iniciarNuevo, 'Nuevo', permss)}
                        end={generic.rightToolbarTemplate(tablaEstado.data, omodel, 'Listado_accionista_pn', 'LISTADO ACCIONISTA PERSONA NATURAL')} />
                    <Card style={{ maxWidth: '100%', margin: 'auto' }}>
                        <TablaDinamica modelo={omodel.model} estadoTabla={tablaEstado} datos={tablaEstado.data} totalRegistros={tablaEstado.totalRecords} dataKey="id" acciones={ColumnaAcciones} />
                    </Card>
                </div>
            )}

            {dialogAccionista &&
                <div className="fadein animation-duration-300">
                    <Panel headerTemplate={panelHeaderTemplate} className="panel-ficha shadow-2">
                        <div ref={topRef} style={{ position: 'absolute', top: '0', left: '0', width: '1px', height: '80px', pointerEvents: 'none', visibility: 'hidden' }} />
                        <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} className="form-tabview">

                            <TabPanel header="Sección 1" rightIcon="pi pi-id-card ml-2">
                                <DatosGenerales toast={toast} catalogos={catalogos} next={handleNext} id={accionista?.id} nuevoAccionista={nuevoAccionista} permss={permss} soloLectura={soloLectura} />
                            </TabPanel>

                            <TabPanel header="Sección 2" disabled={!accionista?.id} rightIcon="pi pi-chart-line ml-2">
                                <InformacionEconomica toast={toast} id={accionista?.id} back={handlePrev} next={handleNext} permss={permss} catalogos={catalogos} soloLectura={soloLectura} />
                            </TabPanel>

                            <TabPanel header="Sección 3" disabled={!accionista?.id} rightIcon="pi pi-shield ml-2">
                                <Pep toast={toast} id={accionista?.id} back={handlePrev} next={handleNext} permss={permss} soloLectura={soloLectura} catalogos={catalogos} />
                            </TabPanel>

                            <TabPanel header="Sección 4" disabled={!accionista?.id} rightIcon="pi pi-file ml-2">
                                <Declaracion toast={toast} id={accionista?.id} back={handlePrev} permss={permss} catalogos={catalogos} setDialogInstruccion={setDialogInstruccion}
                                    mostrarAnexos={() => setDialogAnexos(true)} urls={urls} estado={accionista?.nemonico} reiniciar={reiniciar} soloLectura={soloLectura} />
                            </TabPanel>

                        </TabView>
                    </Panel>

                    {mostrarRegresarFlotante && !siCliente && (
                        <div className="fadein scalein" style={{ position: 'fixed', top: '65px', right: '30px', zIndex: 999 }}>
                            <Button icon="pi pi-arrow-left" onClick={regresar} rounded className="p-button-primary shadow-4" style={{ width: '45px', height: '45px' }} tooltip="Regresar al listado" tooltipOptions={{ position: 'left' }} />
                        </div>
                    )}
                </div>
            }
            <DialogInstrucciones visible={dialogInstruccion} onHide={() => setDialogInstruccion(false)} title={omodel.titulo} items={omodel.pasosInstrucciones} />

            <Dialog visible={dialogAnexos} header="Gestión de Anexos" modal className="p-fluid modal-ssize" onHide={() => setDialogAnexos(false)} style={{ width: '800px' }}>
                <AnexosComponent tipo="accionistaPn" urls={urls} idp={accionista?.id} toast={toast} permss={permss}
                    setDialogSubir={setDialogAnexos} reiniciar={reiniciar} estado={accionista?.nemonico} soloLectura={soloLectura} />
            </Dialog>
        </>
    )
}